/** @jsxImportSource @emotion/react */
import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormatOptionLabelMeta, OptionType } from 'components/select';
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import Drawer from '@atlaskit/drawer';

import { colors, gridSize } from '@atlaskit/theme';
import { useQuery } from 'react-query';
import { CreatableSelect, styles } from '../../../components/select';
import { CompanyOption } from '../../../components/select/CompanyOption';
import api from '../../../libs/api';
import CustomerForm from '../CustomerForm';
import { PageWrapper } from '../../../components/page';
import PageHeader from '@atlaskit/page-header';
import { Grid, GridColumn } from '@atlaskit/page';
import Modal from '../../../components/modal-dialog';
import { FormattedMessage } from 'components/i18n';
import Button, { ButtonGroup } from '@atlaskit/button';
import CustomerEditDrawer from './CustomerEditDrawer';

interface Customer {
  id: string;
  name: string;
  type: 'business' | 'private';
  website: string;
  address?: any;
}

interface OptionProps {
  children: React.ReactNode;
}
const Option = ({ children }: OptionProps) => (
  <div
    css={css`
      //font-size: 1.4em;
    `}
  >
    {children}
  </div>
);

// the text node of the control
const ControlLabel = (option: Customer) => <CompanyOption company={option} />;
// the text node for an option
const OptionLabel = (option: any) => {
  const { name, website, address } = option;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { isNew } = option;

  if (isNew) {
    return (
      <div
        css={css`
          font-weight: 500;
          color: ${colors.primary()};
        `}
      ><FormattedMessage id="sale.CustomerSelect.createNew" defaultMessage={`Create "{name}"`} values={{name}}/></div>
    );
  }
  return (
    <CompanyOption
      company={option as Customer}
      // showDetails
    />
  );
};

// switch formatters based on render context (menu | value)
const formatOptionLabel = (
  option: Customer,
  { context }: FormatOptionLabelMeta<Customer>
) => {
  return context === 'value' ? ControlLabel(option) : OptionLabel(option);
};

const fetchCustomers = async () => api.get('/customers');

function CustomerSelect(props: any) {
  const { value } = props;
  const { data, error, isLoading } = useQuery(['customers'], fetchCustomers);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const [input, setInput] = useState('');

  const ref = useRef<any>();
  const handleInputChange = useCallback((newInput: string) => {
    setInput(newInput);
  }, []);

  const handleChange = useCallback(
    (newValue: any, actionMeta: any) => {
      console.group('Value Changed');
      console.log(newValue);
      console.log(`action: ${actionMeta.action}`);
      console.groupEnd();
      if (props.onChange) {
        props.onChange(newValue, actionMeta);
      }

      if (actionMeta.action === 'create-option') {
        setMenuIsOpen(false);
        setIsDrawerOpen(true);
        ref.current?.blur();
      }
    },
    [ref.current, value?.id]
  );

  const handleCreateOption = useCallback((newOption: string) => {
    console.log('onCreateOption input is', newOption);
    setIsDrawerOpen(true);
    ref.current?.blur();
  }, []);

  const handleMenuOpen = useCallback(
    (args: any) => {
      if (value?.id) {
        setMenuIsOpen(false);
        setIsDrawerOpen(true);
        setTimeout(() => {
          ref.current.blur();
        });
      } else {
        setMenuIsOpen(true);
      }
    },
    [ref.current, value?.id]
  );

  const handleMenuClose = useCallback((args: any) => {
    setMenuIsOpen(false);
    ref.current.blur();
  }, []);

  return (
    <React.Fragment>
      <CustomerEditDrawer
        customer={value}
        isOpen={isDrawerOpen}
        onCancel={() => setIsDrawerOpen(false)}
        onSave={(customer) => {
          setIsDrawerOpen(false);
          props.onChange(customer);
        }}
      />

      <CreatableSelect
        options={data}
        // onCreateOption={handleCreateOption}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => option.name || option.label}
        getOptionValue={(option) => option.id || option.value}
        formatOptionLabel={formatOptionLabel}
        openMenuOnClick
        getNewOptionData={(inputValue: string, optionLabel: string) => {
          return {
            name: inputValue,
            tradeName: inputValue,
            isNew: true,
            type: 'business',
          };
        }}
        blurInputOnSelect
        ref={ref}
        menuIsOpen={menuIsOpen}
        styles={styles}
        isClearable
        focusDefaultOption={false}
        // onClickPreventDefault
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        {...props}
        value={value?.id ? value : null}
        onChange={handleChange}
      />
    </React.Fragment>
  );
}

export default React.memo(CustomerSelect);
